<template>
  <div class="content">
    <div class="members-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-members">

          <button
            v-if="$route.params.id"
            type="button"
            class="glad-back"
            @click="goToAllRtcs">
            &larr; All RTCs
          </button>

          <header class="glad-members__header">
            <p class="glad-eyebrow">{{ eyebrow }}</p>
            <h1 class="glad-members__title">{{ pageTitle }}</h1>
            <p class="glad-members__sub">{{ pageSub }}</p>
          </header>

          <div v-if="loading" class="glad-loading">
            <span class="glad-spinner"></span>
          </div>

          <div v-else-if="error" class="glad-error">
            {{ error }}
          </div>

          <template v-else>
            <div v-if="!members.length" class="glad-empty">
              No members in this RTC yet.
            </div>

            <div v-else class="glad-table-wrap">
              <table class="glad-table">
                <thead>
                  <tr>
                    <th scope="col">Member</th>
                    <th scope="col">Role</th>
                    <th scope="col">Status</th>
                    <th scope="col">Last sign-in</th>
                    <th scope="col">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in members" :key="m.id">
                    <td>
                      <span class="glad-rtc-name">{{ m.name }}</span>
                      <span class="glad-member-email">{{ m.email }}</span>
                    </td>
                    <td>
                      <span class="glad-role-pill">{{ m.role_label }}</span>
                    </td>
                    <td>
                      <span
                        class="glad-status"
                        :class="m.is_active ? 'glad-status--active' : 'glad-status--inactive'">
                        {{ m.is_active ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td>{{ formatDateOrNever(m.last_sign_in) }}</td>
                    <td>{{ formatDateOrNever(m.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RtcMembers',
  data() {
    return {
      loading: true,
      error: '',
      rtc: null,
      members: [],
      canManage: false,
    };
  },
  computed: {
    eyebrow() {
      const base = (this.rtc && this.rtc.short_name) ? this.rtc.short_name : 'RTC';
      if (this.rtc && this.rtc.is_ntc) return base + ' · National';
      if (this.rtc && this.rtc.region) return base + ' · ' + this.rtc.region;
      return base;
    },
    pageTitle() {
      return (this.rtc && this.rtc.name) ? this.rtc.name : 'RTC Members';
    },
    pageSub() {
      return this.members.length + ' member(s)';
    },
  },
  watch: {
    '$route': 'fetch',
  },
  created() {
    this.fetch();
  },
  methods: {
    fetch() {
      this.loading = true;
      this.error = '';
      const id = this.$route.params.id;
      const url = id
        ? 'glad/admin/rtcs/' + id + '/members'
        : 'glad/admin/rtc-members';
      this.$http.get(url)
        .then(resp => {
          this.rtc = resp.data.rtc || null;
          this.members = resp.data.data || [];
          this.canManage = !!resp.data.can_manage;
        })
        .catch(err => {
          const status = err && err.response && err.response.status;
          if (status === 422) {
            this.error = 'No home RTC is associated with your account.';
          } else if (status === 403) {
            this.error = "You don't have access to this roster.";
          } else {
            this.error = 'Could not load the roster.';
          }
        })
        .finally(() => { this.loading = false; });
    },
    goToAllRtcs() {
      this.$router.push('/glad/rtcs');
    },
    formatDate(d) {
      if (!d) return '';
      // Laravel returns "YYYY-MM-DD" or "YYYY-MM-DD HH:MM:SS" without TZ.
      const normalized = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(d)
        ? d
        : d.replace(' ', 'T') + 'Z';
      const parsed = new Date(normalized);
      if (isNaN(parsed.getTime())) return d;
      const now = new Date();
      const diffMs = now.getTime() - parsed.getTime();
      const diffHr = Math.round(diffMs / 3600000);
      if (diffHr < 1)  return 'just now';
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.round(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;
      try {
        return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        return d;
      }
    },
    formatDateOrNever(d) {
      if (!d) return 'Never';
      return this.formatDate(d);
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.members-wrap { width: 100%; }
.members-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
}
.glad-members {
  padding: 1.5rem 1.5rem 2.5rem;
  font-family: var(--glad-font-sans);
}

/* Back link ---------------------------------------------------------------- */
.glad-back {
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-family: var(--glad-font-sans);
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--glad-text-soft);
  margin-bottom: 1rem;
  display: inline-block;
  transition: color 0.15s ease;

  &:hover { color: var(--glad-ink); }
}

/* Header ------------------------------------------------------------------- */
.glad-members__header { margin-bottom: 1.75rem; }
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin: 0 0 0.45rem;
}
.glad-members__title {
  font-family: var(--glad-font-serif);
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.35rem;
  line-height: 1.2;
}
.glad-members__sub {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0;
}

/* Loading + error ---------------------------------------------------------- */
.glad-loading {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.glad-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: inline-block;
}
@keyframes glad-spin { to { transform: rotate(360deg); } }

.glad-error {
  padding: 2.5rem 1rem;
  text-align: center;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius);
  color: var(--glad-text-soft);
  font-size: 0.9rem;
}

.glad-empty {
  font-size: 0.9rem;
  color: var(--glad-text-soft);
  text-align: center;
  padding: 1.5rem 0;
}

/* Table -------------------------------------------------------------------- */
.glad-table-wrap {
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius);
  overflow: hidden;
}
.glad-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th, td {
    text-align: left;
    padding: 0.65rem 0.85rem;
    border-bottom: 1px solid var(--glad-line);
    vertical-align: middle;
  }
  tr:last-child td { border-bottom: 0; }

  thead th {
    background: var(--glad-paper);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--glad-text-soft);
  }
}
.glad-rtc-name {
  display: block;
  font-weight: 600;
  color: var(--glad-ink);
}
.glad-member-email {
  display: block;
  margin-top: 0.15rem;
  font-size: 0.74rem;
  color: var(--glad-text-muted);
}

/* Role pill ---------------------------------------------------------------- */
.glad-role-pill {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  background: var(--glad-paper-2);
  color: var(--glad-text-soft);
}

/* Status pill -------------------------------------------------------------- */
.glad-status {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;

  &--active {
    background: rgba(180, 120, 20, 0.12);
    color: var(--glad-accent);
  }
  &--inactive {
    background: rgba(15, 17, 21, 0.06);
    color: var(--glad-text-muted);
  }
}
</style>
